import { useEdenredToken } from '../../../../../login/external/edenredUserManager';
import {
  AddPaymentMethodRequest,
  PaymentMethodType,
  PaymentPartnerType,
  useAddPaymentMethodV2Mutation,
} from '../../../../../codegen/vehicles';
import { useAppSelector } from '../../../../../state/hooks';
import {
  getPaymentPartnerType,
  getSelectedVehicleId,
} from '../../../state/onboardingUiSlice';
import { isNil } from 'lodash';
import { useAvailableChargeAndGoCards } from '../../../../../state/paymentPartners/useAvailableChargeAndGoCards';
import moment from 'moment';
import { useLoadedVehicles } from '../../../../../state/vehicles/useVehicles';
import { AddNewServiceCardForm } from './AddNewServiceCardForm';
import { UseFormReturn } from 'react-hook-form';

export const useCreateServiceCard = () => {
  const { updateVehicle } = useLoadedVehicles();
  const edenredToken = useEdenredToken();
  const selectedPaymentPartnerType = useAppSelector(getPaymentPartnerType);
  const { availableCards: availableChargeAndGoCards } =
    useAvailableChargeAndGoCards({
      skip:
        selectedPaymentPartnerType !== PaymentPartnerType.ManChargeAndGoPayment,
    });
  const [addPaymentMethod, { error, isLoading, reset }] =
    useAddPaymentMethodV2Mutation();
  const selectedVehicleId = useAppSelector(getSelectedVehicleId);

  const extractCardDataFromForm = (
    form: UseFormReturn<AddNewServiceCardForm>,
  ): AddPaymentMethodRequest => {
    const cardDetails = form.getValues('cardDetails');
    const selectedCardId = form.getValues('selectedCardId');

    const parsedExpiry = cardDetails?.expiryDate
      ? moment(cardDetails.expiryDate, 'MM/YYYY').format('MM-YY')
      : undefined;

    switch (selectedPaymentPartnerType) {
      case PaymentPartnerType.UtaPayment: {
        return {
          referenceId: cardDetails!.cardNumber,
          paymentPartner: selectedPaymentPartnerType,
          type: PaymentMethodType.FuelCard,
          cardNumber: cardDetails?.cardNumber!,
          expiry: parsedExpiry,
          onboardingDetails: !isNil(edenredToken)
            ? { userToken: edenredToken }
            : undefined,
        };
      }
      case PaymentPartnerType.MockFuelingPayment: {
        return {
          referenceId: cardDetails!.cardNumber,
          paymentPartner: selectedPaymentPartnerType,
          type: PaymentMethodType.FuelCard,
          cardNumber: cardDetails?.cardNumber!,
          expiry: parsedExpiry,
        };
      }
      case PaymentPartnerType.ManChargeAndGoPayment: {
        return {
          referenceId: selectedCardId!,
          paymentPartner: selectedPaymentPartnerType,
          type: PaymentMethodType.ChargeCard,
          cardNumber: availableChargeAndGoCards.find(
            (card) => card.referenceId === selectedCardId,
          )!.cardNumber,
        };
      }
      case PaymentPartnerType.DkvPayment: {
        return {
          referenceId: selectedCardId!,
          paymentPartner: selectedPaymentPartnerType,
          type: PaymentMethodType.FuelCard,
          cardNumber: '',
        };
      }
      default: {
        throw Error(
          `Unknown payment partner type ${selectedPaymentPartnerType}`,
        );
      }
    }
  };

  const createServiceCard = async (
    form: UseFormReturn<AddNewServiceCardForm>,
  ): Promise<boolean> => {
    if (!selectedVehicleId) {
      throw Error('No vehicle selected');
    }

    const result = await addPaymentMethod({
      vehicleId: selectedVehicleId,
      addPaymentMethodRequest: extractCardDataFromForm(form),
    });

    if ('error' in result) {
      // error gets handled inside RTK toolkit, no need to raise.
      return false;
    }

    const paymentMethod = result.data;

    updateVehicle(selectedVehicleId, (vehicle) => {
      vehicle.paymentMethods.push(paymentMethod);
      return vehicle;
    });

    return true;
  };

  return {
    createServiceCard,
    error,
    isLoading,
    reset,
  };
};
